import React, { useEffect, useState } from "react";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import type { AlertItem } from "@/types/alert";

//#region Type
interface AlertProps {
  message: AlertItem;
  onRemove: (id: number) => void;
}

enum AnimationStatus {
  Entering = "entering",
  Visible = "visible",
  Exiting = "exiting",
}
//#endregion

const fadeInDuration = 500;
const visibleDuration = 3000;
const fadeOutDuration = 500;

const alertStyle = {
  success: {
    icon: <CheckCircleIcon />,
    txtColor: "text-success-content",
    bgColor: "bg-success",
  },
  info: {
    icon: <InformationCircleIcon />,
    txtColor: "text-info-content",
    bgColor: "bg-info",
  },
  warning: {
    icon: <ExclamationTriangleIcon />,
    txtColor: "text-warning-content",
    bgColor: "bg-warning",
  },
  error: {
    icon: <ExclamationCircleIcon />,
    txtColor: "text-error-content",
    bgColor: "bg-error",
  },
};

const Alert = ({ message, onRemove }: AlertProps) => {
  const autoCloseEnabled = message.autoClose ?? true;

  const { bgColor, icon, txtColor } = alertStyle[message.type];

  const [animationStatus, setAnimationStatus] = useState<AnimationStatus>(AnimationStatus.Entering);

	const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClose = () => {
    setAnimationStatus(AnimationStatus.Exiting);

    timerRef.current = setTimeout(() => {
      onRemove(message.id);
      message.onClose?.();
    }, fadeOutDuration);
  };

  useEffect(() => {
    if (!autoCloseEnabled) return;

    // 設置進場、可見、離場的計時器
    const enterTimer = setTimeout(
      () => setAnimationStatus(AnimationStatus.Visible),
      fadeInDuration,
    );

    const exitTimer = setTimeout(
      () => setAnimationStatus(AnimationStatus.Exiting),
      fadeInDuration + visibleDuration,
    );

    const removeTimer = setTimeout(
      () => {
        onRemove(message.id);
        message.onClose?.();
      },
      fadeInDuration + visibleDuration + fadeOutDuration,
    );

    return () => {
      clearTimeout(enterTimer);
      clearTimeout(exitTimer);
      clearTimeout(removeTimer);

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [message.id]);

  return (
    <div
      className={clsx(bgColor, "flex justify-between rounded-lg py-2 align-middle shadow-2xl", {
        "hs-animate-fade-in-down": animationStatus === AnimationStatus.Entering,
        "opacity-100": animationStatus === AnimationStatus.Visible,
        "hs-animate-fade-out-up": animationStatus === AnimationStatus.Exiting,
      })}
    >
      <div className="flex items-center">
        {React.cloneElement(icon, {
          className: clsx(icon.props.className, txtColor, "box-content size-6 p-2 shrink-0"),
        })}
        <div className={clsx(txtColor, "flex flex-col text-left font-normal")}>
          <div className="brand-text-md">{message.title}</div>
          {message.desc && <p className="brand-text-sm">{message.desc}</p>}
        </div>
      </div>
      <button onClick={handleClose}>
        <XMarkIcon className={clsx(txtColor, "box-content size-6 p-2")} />
      </button>
    </div>
  );
};

export default Alert;
